<template>
    <v-form ref="form" v-model="formValid">
        <v-expand-transition>
            <banner class="mb-5" v-if="showAllDaysOffError" text="The scheduled dates conflict with “School Days Off.” Please adjust the schedule before proceeding." usage="warning" />
        </v-expand-transition>
        <div>
            <fe-label>Schedule Type</fe-label>
            <v-radio-group dense row hide-details v-model="localValue.frequency_cd" class="creation-intervention-schedule-radio-group">
                <v-radio v-for="schedule in scheduleTypes" :value="schedule.id" :key="schedule.id" style="height:35px;" :data-test="`create-intervention-schedule-type-${$toKebabCase(schedule.name)}`">
                    <template v-slot:label>
                        <span style="font-size: 12px;">{{ schedule.name }}</span> 
                    </template>
                </v-radio>
            </v-radio-group>
        </div>
        
        <div class="d-flex mt-3" style="align-items: flex-end;">
            <fe-date-picker
                label="Start Date"
                v-model="localValue.start_date"
                :rules="rules.startDate"
                :min="minStartDate"
                class="mr-2 flex-grow-1"
                dateFormat="M/D/YYYY"
                prependIcon="far fa-calendar-day"
                data-test="create-intervention-schedule-start-date"
                style="width: 50%"
            />
            <div v-if="localValue.frequency_cd === 'WEEK'" class="d-flex align-center" style="width: 100%">
                <div style="width: 150px" class="ml-2">
                    <fe-label>Duration in Weeks</fe-label>
                    <v-text-field
                        v-model="localValue.duration"
                        flat solo dense
                        type="number"
                        :rules="$fieldValidators('number', 'Duration', { min: 1, required: true })"
                        data-test="create-intervention-schedule-duration-in-weeks"
                    />
                </div>
                <div class="ml-4" v-if="localValue.duration">
                    to {{ localValue.end_date }}
                </div>
            </div>
        </div>

        <div key="weekly" v-if="localValue.frequency_cd === 'WEEK'">
            <weekday-picker v-model="localValue.week_days" required threeLetter secondary data-test="create-intervention-schedule-weekday-picker"/>
        </div>

        <div key="days-on-off" v-if="localValue.frequency_cd === 'BLOCK'" class="d-flex">
            <div class="flex-grow-1 pr-2">
                <fe-label>Days On</fe-label>
                <v-text-field
                    v-model="localValue.frequency_cnt"
                    flat solo dense
                    type="number"
                    :rules="$fieldValidators('number', 'Days on', { min: 1, max: 14, required: true })"
                    data-test="create-intervention-schedule-days-on"
                />
            </div>

            <div class="flex-grow-1">
                <fe-label>Days Off</fe-label>
                <v-text-field
                    v-model="localValue.secondary_cnt"
                    flat solo dense
                    type="number"
                    :rules="$fieldValidators('number', 'Days off', { min: 1, max: 14 })"
                    data-test="create-intervention-schedule-days-off"
                />
            </div>
        </div>

         <div key="number-of-sessions" v-if="localValue.frequency_cd === 'NOSCHED'" class="d-flex">
            <div class="flex-grow-1">
                <fe-label>Total Number of Sessions</fe-label>
                <v-text-field
                    v-model="localValue.frequency_cnt"
                    flat solo dense
                    type="number"
                    :rules="$fieldValidators('number', 'Number of sessions', { min: 1, max: 200, required: true })"
                    data-test="create-intervention-schedule-total-sessions"
                />
            </div>
        </div>

        <div key="specific-dates" v-if="localValue.frequency_cd === 'SPECIFIC'">
            <div style="border: 1px solid #E0E1E6; border-radius: 4px; display:flex; padding: 10px;" class="mb-5">
                <v-date-picker 
                    v-model="datesSelected[currentMonth]"
                    @input="updateDatesSelected" 
                    multiple no-title color="#005673" 
                    class="first-month-picker" 
                    :picker-date="currentMonth"
                    @update:picker-date="currentMonth = $event"
                    :min="minDate"
                    :max="maxDate"
                    data-test="create-intervention-schedule-specific-dates-first-month"
                    :allowed-dates="allowedDates"
                />
                <v-date-picker 
                    v-model="datesSelected[nextMonth]" 
                    @input="updateDatesSelected" 
                    multiple no-title color="#005673" 
                    :picker-date="nextMonth"
                    @update:picker-date="nextMonth = $event"
                    class="next-month-picker"
                    :min="minDate"
                    :max="maxDate"
                    data-test="create-intervention-schedule-specific-dates-next-month"
                    :allowed-dates="allowedDates"
                />
            </div>

            <div v-if="localValue && localValue.specific_dates && localValue.specific_dates.length > 0" class="mb-3">
                <div style="font-family: 'HyperlegibleSans-Medium', Hyperlegible Sans, 'Roboto', sans-serif" class="mb-3">{{ localValue.specific_dates.length }} Dates Added</div>
                <div class="d-flex" style="flex-wrap:wrap;">
                    <div v-for="(date, i) in localValue.specific_dates" class="added-date d-flex mr-2 mb-2" :key="`date-`+i">
                        <div class="date-name">{{ $dayjs(date).format('M/D/YYYY') }}</div>
                        <div class="ml-auto">
                            <fe-icon-btn small useIcon="fal fa-times" 
                                @click="removeDate(date)" 
                                data-test="create-intervention-schedule-remove-specific-date" 
                                @keydown.enter="removeDate(date)" 
                                @keydown.space.prevent="removeDate(date)"
                                aria-label="Remove specific date"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex">
            <div class="pr-2 flex-grow-1">
                <fe-label>Start Time <span v-if="isAttendance">(Optional)</span></fe-label>
                <fe-time-scroll-picker
                    v-model="localValue.start_time"
                    :rules="$fieldValidators('text', 'Start time', { required: isAttendance ? false : true })"
                    data-test="create-intervention-schedule-start-time"
                />
            </div>

            <div class="flex-grow-1">
                <fe-label>End Time <span v-if="isAttendance">(Optional)</span></fe-label>
                <fe-time-scroll-picker
                    v-model="localValue.end_time"
                    :rules="rules.endTime"
                    data-test="create-intervention-schedule-end-time"
                />
            </div>
        </div>

        <div v-if="!standalone" class="d-flex card-bottom">
            <fe-btn usage="secondary" @click="$emit('go','back')" data-test="create-intervention-schedule-back">Back</fe-btn>
            <fe-btn @click="nextStep" data-test="create-intervention-schedule-step-add" :disabled="disableNextStep">Continue</fe-btn>
        </div>
    </v-form>
</template>

<script>
    import WeekdayPicker from '@/components/common/button/WeekdayPicker'
    import timeMixin from '../mixins/time'
    import Banner from '@/components/common/Banner'
    import { mapState } from 'vuex'

    export default {
        name: 'Schedule',
        components: {
            Banner,
            WeekdayPicker
        },
        mixins: [timeMixin],
        props: {
            value: {
                required: true
            },
            standalone: {
                type: Boolean,
                default: false
            },
            timeRequired: {
                type: Boolean,
                default: true,
            },
            validForm: {
                type: Boolean,
                default: true,
            },
            students: {
                type: Array,
                default: () => [],
            },
            isAttendance: {
                type: Boolean,
                default: false,
            },
            // Earliest date the start may be set to. Null leaves the start date unbounded.
            minStartDate: {
                type: String,
                default: null,
            }
        },
        computed: {
            ...mapState('global', ['currentYear']),
            showByParticipation() {
                return this.$store.getters['flags/flags']['ec-interventions-by-participation'] === true
            },
            minDate() {
                return this.localValue.start_date ? new Date(this.localValue.start_date).toISOString().substring(0, 10) : null
            },
            maxDate() {
                return this.localValue.end_date ? new Date(this.localValue.end_date).toISOString().substring(0, 10) : null
            },
            invalidEndDate() {
                if (!this.localValue.end_date || !this.localValue.start_date) return true

                return this.$dayjs(this.localValue.end_date).isBefore(this.localValue.start_date)
            },
            isValid() {
                if (!this.formValid) return false
                let schedType = this.localValue.frequency_cd
                if (!schedType) return false
                if (this.invalidEndDate) return false
                let valid = true

                switch (schedType) {
                    case 'WEEK':
                        valid = this.$checkAllKeys(this.localValue, ['start_date', 'end_date', 'frequency_cd', 'week_days', 'duration'], true)
                        break
                    case 'BLOCK':
                        valid = this.$checkAllKeys(this.localValue, ['start_date', 'end_date', 'frequency_cd', 'frequency_cnt', 'secondary_cnt'], true)
                        break
                    case 'NOSCHED':
                        valid = this.$checkAllKeys(this.localValue, ['start_date', 'end_date', 'frequency_cd', 'frequency_cnt'], true)
                        break
                    case 'SPECIFIC':
                        valid = this.$checkAllKeys(this.localValue, ['start_date', 'end_date', 'frequency_cd', 'specific_dates'], true)
                        break
                }
                return valid
            },
        },
        data() {
            return {
                localValue: {
                    user_id: null,
                    frequency_cd: 'WEEK',
                    week_days: 'MTWHF',
                    start_time: null,
                    end_time: null,
                    start_date: null,
                    end_date: null,
                    specific_dates: [],
                    duration: null,
                    frequency_cnt: null,
                    secondary_cnt: null,
                },
                autoSuggested: false,
                scheduleTypes: [{
                    id: 'WEEK',
                    name: 'Weekly Schedule'
                }, {
                    id: 'BLOCK',
                    name: 'Block Schedule'
                }, {
                    id: 'NOSCHED',
                    name: 'As Needed'
                },{
                    id: 'SPECIFIC',
                    name: 'Specific Dates'
                }],
                showAllDaysOffError: false,
                disableNextStep: false,
                rules: {
                    // The picker's :min greys out earlier days, but the text field beside it does
                    // not honour :min, so a typed date needs the same bound.
                    startDate: [
                        v => !this.minStartDate || !this.$dayjs(v).isBefore(this.minStartDate)
                            || `Must come on or after ${this.$dayjs(this.minStartDate).format('M/D/YYYY')}`
                    ].concat(this.$fieldValidators('text', 'Start date', { required: true })),
                    endDate: [
                        v => this.localValue.start_date ? (this.$dayjs(v).isBefore(this.localValue.start_date) ? 'Must come on or after start date' : true) : true
                    ].concat(this.$fieldValidators('text', 'End date', { required: true })),
                    endTime: [
                        v => !this.$dayjs('2000-01-01 ' + v).isBefore('2000-01-01 ' + this.localValue.start_time) || 'Not a valid End Time'
                    ].concat(this.$fieldValidators('text', 'End time', { required: this.isAttendance ? false : true }))
                },
                currentMonth: null,
                nextMonth: null,
                formValid: false,
                datesSelected: [],
            }
        },
        watch: {
            isValid: {
                handler(v) {
                    this.$emit('valid', v)
                },
                immediate: true
            },
            localValue: {
                deep: true,
                handler(v) {
                    this.$emit('input', v)
                }
            },
            'localValue.start_time'(startTime) {
                this.localValue.end_time = this.getEndTime(startTime, this.localValue.end_time)
            },
            value(v) {
                this.localValue = v
            },
            currentMonth(v) {
                if(v) {
                    let next = this.$dayjs(v)
                    this.nextMonth = this.$dayjs(next).add(1, 'month').toISOString()
                }
            },
            nextMonth(v) {
                if(v) {
                    let prev = this.$dayjs(v)
                    this.currentMonth = this.$dayjs(prev).subtract(1, 'month').toISOString()
                }
            },
            'localValue.duration'(v) {
                if (this.localValue.frequency_cd !== 'WEEK') return
                this.localValue.end_date = v && this.localValue.start_date ? this.$dayjs(this.localValue.start_date).add(this.localValue.duration, 'week').format('M/D/YYYY') : null
            },
            'localValue.frequency_cd'(v) {
                // want to reset the end date if updating/new phase so that calendar becomes open and not greyed out
                if (v === 'SPECIFIC' && this.standalone) {
                    this.localValue.end_date = this.currentYear ? this.$dayjs(this.currentYear.year_end, 'YYYY-MM-DD').format('M/D/YYYY') : null
                }
            }
        },
        methods: {
            async checkIfAllDaysOff() {
                return new Promise((resolve, reject) => {
                    if (!this.localValue.frequency_cd || !this.localValue.start_date || !this.localValue.end_date) {
                        return resolve(false)
                    }

                    let params = {
                        start_date: this.$dayjs(this.localValue.start_date).format('YYYY-MM-DD'),
                        end_date: this.$dayjs(this.localValue.end_date).format('YYYY-MM-DD'),
                        frequency: this.localValue.frequency_cd,
                        student_id: this.students?.[0]?.student_id || 0,
                    }

                    switch (this.localValue.frequency_cd) {
                        case 'WEEK':
                            if (!this.localValue.week_days) {
                                return resolve(false)
                            }
                            params.week_days = this.localValue.week_days
                            break
                        case 'BLOCK':
                            if (!this.localValue.frequency_cnt || !this.localValue.secondary_cnt) {
                                return resolve(false)
                            }
                            params.days_on = this.localValue.frequency_cnt
                            params.days_off = this.localValue.secondary_cnt
                            break
                        case 'NOSCHED':
                            return resolve(true) // no need to check server
                        case 'SPECIFIC':
                            return resolve(true) // no need to check server
                    }

                    this.$axios.get(this.$models.getUrl('interventionScheduleDays', 'resource', params)).then(response => {
                        resolve((response.data?.dayCount || 0) > 0)
                    })
                })
            },
            invalidEndTime() {
                if ((this.timeRequired || this.localValue.start_time) && !this.localValue.end_time) return true

                let start = '2000-01-01 ' + this.localValue.start_time
                return this.$dayjs('2000-01-01 ' + this.localValue.end_time).isBefore(start)
            },
            async nextStep(e) {
                this.disableNextStep = true
                if (!this.$refs.form.validate()) {
                    this.disableNextStep = false
                    return
                }
                let allDaysOff = await this.checkIfAllDaysOff()
                if (!allDaysOff) {
                    this.showAllDaysOffError = true
                    this.disableNextStep = false
                    return
                }
                this.showAllDaysOffError = false
                this.clearUnusedFields()
                this.$emit('go')
            },
            clearUnusedFields() {
                // clear values left over from other schedule types
                switch (this.localValue.frequency_cd) {
                    case 'WEEK':
                        this.localValue.specific_dates = []
                        this.localValue.frequency_cnt = null
                        this.localValue.secondary_cnt = null
                        break
                    case 'SPECIFIC':
                        this.localValue.frequency_cnt = null
                        this.localValue.secondary_cnt = null
                        this.localValue.week_days = 'MTWHF'
                        break
                    case 'BLOCK':
                        this.localValue.specific_dates = []
                        this.localValue.week_days = 'MTWHF'
                        break
                    case 'NOSCHED':
                        this.localValue.specific_dates = []
                        this.localValue.secondary_cnt = null
                        this.localValue.week_days = 'MTWHF'
                        break
                }
            },
            removeDate(date) {
                Object.keys(this.datesSelected).forEach(key => {
                    if(this.datesSelected[key].includes(date)) {
                        this.datesSelected[key].splice(this.datesSelected[key].indexOf(date), 1)
                    }
                })
                this.updateDatesSelected()
            },
            updateDatesSelected() {
                let dates = []
                Object.keys(this.datesSelected).forEach(date => {
                    dates.push(...this.datesSelected[date])
                })
                this.localValue.specific_dates = dates
                this.localValue.specific_dates = this.localValue.specific_dates.sort((a, b) => this.$dayjs(a).isBefore(this.$dayjs(b)) ? -1 : (this.$dayjs(a).isAfter(this.$dayjs(b)) ? 1 : 0))
            },
            allowedDates(val) {
                // only allow selecting weekdays
                const day = new Date(val).getDay()
                return day !== 5 && day !== 6
            }
        },
        created() {
            // pull previous values into localValue
            if (this.value) {
                this.localValue = this.$_.assign(this.localValue, this.value)
                // clear stale values saved from a previous schedule type (BE doesnt always save/return nulls)
                this.clearUnusedFields()
            }
            if (!this.localValue.start_date) this.localValue.start_date = this.$dayjs().format('M/D/YYYY')
            else this.localValue.start_date = this.$dayjs(this.localValue.start_date).format('M/D/YYYY')
            if (!this.localValue.end_date) this.localValue.end_date = this.currentYear ? this.$dayjs(this.currentYear.year_end, 'YYYY-MM-DD').format('M/D/YYYY') : this.localValue.start_date
            else this.localValue.end_date = this.$dayjs(this.localValue.end_date).format('M/D/YYYY')
            this.currentMonth = new Date().toISOString().slice(0, 7)
            if(this.standalone) {
                if(this.localValue.end_date && this.localValue.frequency_cd === 'WEEK') {
                    this.localValue.duration = this.$dayjs(this.localValue.end_date).diff(this.$dayjs(this.localValue.start_date), 'week')
                }
            }
        },
        mounted() {
            // hydrate datesSelected from existing specific_dates (e.g. when editing)
            let selected = this.localValue.specific_dates || this.value.specific_dates || []
            if (selected.length) {
                selected.forEach(date => {
                    const d = this.$dayjs(date)
                    if (!d.isValid()) return
                    const month = d.startOf('month').toISOString()
                    const normalized = d.format('YYYY-MM-DD')
                    if (!this.datesSelected[month]) {
                        this.$set(this.datesSelected, month, [])
                    }
                    if (!this.datesSelected[month].includes(normalized)) {
                        this.datesSelected[month].push(normalized)
                    }
                })
            }
        }
    }
</script>

<style lang="scss" scoped>
.invalid-date {
    ::v-deep .v-input__slot {
        border: 1px solid #BF0D00 !important;
    }
}
.first-month-picker {
    ::v-deep .v-btn:has(.mdi-chevron-right) {
        visibility: hidden;
        pointer-events: none;
    }
    ::v-deep .v-picker__body {
        width: 250px !important;
    }
}
.next-month-picker {
    ::v-deep .v-btn:has(.mdi-chevron-left) {
        visibility: hidden;
        pointer-events: none;
    }
    ::v-deep .v-picker__body {
        width: 250px !important;
    }
}
.added-date {
    background: #ECEDF1;
    border-radius: 4px;
    height: 30px;
    width: fit-content;
    ::v-deep i {
        height: 20px !important;
        width: 20px !important;
        font-size: 14px !important;
    }
    ::v-deep .v-btn {
        margin: 2px !important;
    }

    .date-name {
        line-height: 30px;
        padding-left: 16px;
    }
}

.creation-intervention-schedule-radio-group {
    ::v-deep .v-input__slot {
        padding: 0 !important;
    }
}
</style>
